/**
 * AuthGuard — gate for authenticated app routes.
 *
 * Rules
 * ─────
 * • Real user session (``accessToken`` present)  → render as-is.
 * • Guest onboarding session active              → allow ``/command-center``
 *                                                  (their personalized guest
 *                                                  workspace), ``/academy``,
 *                                                  and ``/workspaces/new`` to
 *                                                  render. Every other
 *                                                  dashboard route soft-
 *                                                  redirects to
 *                                                  ``/command-center`` so
 *                                                  the visitor stays inside
 *                                                  their workspace.
 * • No token, no guest                            → redirect to ``/welcome``.
 *
 * Public routes (``/welcome``, ``/scanner``, ``/demo``, ``/onboarding``,
 * ``/login``, ``/auth/callback``) are not wrapped by this guard — they
 * live outside ``RootLayout`` in the router.
 */
import { Navigate, Outlet, useLocation } from "react-router-dom";

import { useAuthStore } from "@/entities/auth/store";

/**
 * Dashboard routes a guest is allowed to see. Everything else redirects
 * them back to their personalized Home so the story stays cohesive.
 *
 * Post-Harmony: ``/home`` is the canonical authenticated landing.
 * ``/command-center`` remains a legacy alias that navigates to ``/home``.
 */
const GUEST_ALLOWED = new Set<string>([
  "/home",
  "/command-center",
  "/academy",
  "/campaigns",
  "/campaigns/new",
  "/vault",
  "/publishing",
  "/workspace",
]);

export function AuthGuard(): JSX.Element {
  const token = useAuthStore((s) => s.accessToken);
  const isPublicDemoUser = useAuthStore((s) => s.user?.is_public_demo_user === true);
  const guestActive = useAuthStore((s) => s.guest.active);
  const location = useLocation();

  // Full user session — render the requested route.
  if (token) {
    if (isPublicDemoUser && location.pathname === "/genesis") {
      return <Navigate to="/login?mode=register&from=genesis&returnTo=%2Fgenesis" replace />;
    }
    return <Outlet />;
  }

  // Guest session — allow their personalized surfaces through.
  if (guestActive) {
    if (GUEST_ALLOWED.has(location.pathname)) return <Outlet />;
    // Every other dashboard URL routes back to the guest workspace Home so
    // they never see an empty / half-loaded shell.
    return <Navigate to="/home" replace state={{ from: location.pathname }} />;
  }

  // No session at all — go back to the marketing landing.
  // (Patch 34.1.10 restored ``/welcome`` as the default for all
  // visitors; both demo experiences are explicit opt-ins.)
  const returnTo = `${location.pathname}${location.search}${location.hash}`;
  return <Navigate to={`/login?returnTo=${encodeURIComponent(returnTo)}`} replace />;
}
